import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import Seo from "@/components/Seo";

const INVESTIMENTO = 90000;
const TAN = 0.065;
const MARGINE = 0.35;
const DURATE = [12, 24, 36, 48, 60, 72, 84];

const voci = [
  { label: "Fee di ingresso", valore: 15000 },
  { label: "Attrezzature MICHELCHEF®", valore: 35000 },
  { label: "Arredi e Food-Box Wall", valore: 18000 },
  { label: "Installazione", valore: 12000 },
  { label: "Marketing di lancio", valore: 7000 },
  { label: "Imprevisti", valore: 3000 },
];

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const Simulatore = () => {
  const [durata, setDurata] = useState(60);
  const [anticipo, setAnticipo] = useState(0);
  const [scontrini, setScontrini] = useState(80);
  const [ticket, setTicket] = useState(12);

  const calc = useMemo(() => {
    const capitale = Math.max(INVESTIMENTO - anticipo, 0);
    const r = TAN / 12;
    const rata = capitale > 0 ? (capitale * r) / (1 - Math.pow(1 + r, -durata)) : 0;
    const interessi = rata * durata - capitale;
    const fatturato = scontrini * ticket * 30;
    const margine = fatturato * MARGINE;
    const utile = margine - rata;
    const breakEven = Math.ceil(rata / (ticket * MARGINE * 30));
    const payback = utile > 0 ? Math.ceil(INVESTIMENTO / utile) : null;
    let saldo = capitale;
    const piano = [];
    for (let m = 1; m <= Math.min(6, durata); m++) {
      const intM = saldo * r;
      const capM = rata - intM;
      saldo -= capM;
      piano.push({ m, rata, interessi: intM, capitale: capM, saldo: Math.max(saldo, 0) });
    }
    return { capitale, rata, interessi, fatturato, margine, utile, breakEven, payback, piano };
  }, [durata, anticipo, scontrini, ticket]);

  const labelCls = "flex items-center justify-between text-xs uppercase tracking-[0.2em] text-forest/60";
  const sliderCls = "mt-4 w-full accent-[#2FA44F]";

  return (
    <div data-testid="simulatore-page" className="pt-20">
      <Seo
        title="Simulatore Investimento Risorice — Rata e Break-even | COMMPAQ"
        description="Calcola rata mensile, break-even in scontrini al giorno e payback del tuo ristorante automatizzato Risorice: investimento chiavi in mano da € 90.000."
        image="/risorice/foodbox.jpg"
      />
      <section className="bg-forest py-24 text-bone lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="text-sm uppercase tracking-[0.3em] text-sage">Franchising Risorice®</p>
          <h1
            data-testid="simulatore-heading"
            className="mt-6 font-display text-5xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Simula il tuo</MaskedLine>
            <MaskedLine delay={0.3} className="text-moss">
              investimento.
            </MaskedLine>
          </h1>
          <FadeUp delay={0.55} className="mt-8 max-w-xl">
            <p className="leading-relaxed text-bone/70">
              Investimento chiavi in mano di {fmt(INVESTIMENTO)}, finanziabile a TAN 6,5%.
              Muovi i cursori e scopri rata mensile, break-even e payback del tuo Risorice.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <div className="border border-forest/10 bg-white p-8 sm:p-10">
              <h2 className="font-display text-2xl font-bold text-forest">I tuoi parametri</h2>

              <div className="mt-10">
                <p className={labelCls}>
                  <span>Durata finanziaria</span>
                  <span data-testid="durata-value" className="font-display text-base font-bold text-forest">
                    {durata} mesi
                  </span>
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {DURATE.map((d) => (
                    <button
                      key={d}
                      type="button"
                      data-testid={`durata-${d}`}
                      onClick={() => setDurata(d)}
                      className={`rounded-full px-5 py-2 text-sm transition-[background-color,color] duration-300 ${
                        durata === d
                          ? "bg-moss text-bone"
                          : "border border-forest/15 text-forest/70 hover:border-moss hover:text-moss"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <label htmlFor="anticipo" className={labelCls}>
                  <span>Anticipo</span>
                  <span data-testid="anticipo-value" className="font-display text-base font-bold text-forest">
                    {fmt(anticipo)}
                  </span>
                </label>
                <input
                  id="anticipo"
                  type="range"
                  min="0"
                  max="30000"
                  step="1000"
                  value={anticipo}
                  onChange={(e) => setAnticipo(Number(e.target.value))}
                  data-testid="slider-anticipo"
                  className={sliderCls}
                />
              </div>

              <div className="mt-10">
                <label htmlFor="scontrini" className={labelCls}>
                  <span>Scontrini al giorno</span>
                  <span data-testid="scontrini-value" className="font-display text-base font-bold text-forest">
                    {scontrini}
                  </span>
                </label>
                <input
                  id="scontrini"
                  type="range"
                  min="10"
                  max="200"
                  step="5"
                  value={scontrini}
                  onChange={(e) => setScontrini(Number(e.target.value))}
                  data-testid="slider-scontrini"
                  className={sliderCls}
                />
              </div>

              <div className="mt-10">
                <label htmlFor="ticket" className={labelCls}>
                  <span>Ticket medio</span>
                  <span data-testid="ticket-value" className="font-display text-base font-bold text-forest">
                    {fmt(ticket)}
                  </span>
                </label>
                <input
                  id="ticket"
                  type="range"
                  min="8"
                  max="20"
                  step="0.5"
                  value={ticket}
                  onChange={(e) => setTicket(Number(e.target.value))}
                  data-testid="slider-ticket"
                  className={sliderCls}
                />
              </div>

              <div className="mt-10 border-t border-forest/10 pt-8">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/60">
                  Cosa comprendono i {fmt(INVESTIMENTO)}
                </p>
                <ul className="mt-5 space-y-3">
                  {voci.map((v) => (
                    <li key={v.label} className="flex items-center justify-between text-sm">
                      <span className="text-forest/70">{v.label}</span>
                      <span className="font-medium text-forest">{fmt(v.valore)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="bg-forest p-8 text-bone sm:col-span-2 sm:p-10">
                <p className="text-xs uppercase tracking-[0.2em] text-sage">Rata mensile</p>
                <p data-testid="risultato-rata" className="mt-4 font-display text-6xl font-extrabold text-ochre">
                  {fmt(calc.rata)}
                </p>
                <p className="mt-4 text-sm text-bone/60">
                  Finanziando {fmt(calc.capitale)} in {durata} mesi a TAN 6,5% · Interessi totali{" "}
                  <span data-testid="risultato-interessi" className="text-bone">
                    {fmt(calc.interessi)}
                  </span>
                </p>
              </div>

              <div className="border border-forest/10 bg-white p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/60">Fatturato mensile stimato</p>
                <p data-testid="risultato-fatturato" className="mt-3 font-display text-3xl font-extrabold text-forest">
                  {fmt(calc.fatturato)}
                </p>
                <p className="mt-2 text-xs text-forest/55">
                  {scontrini} scontrini/giorno × {fmt(ticket)} × 30 giorni
                </p>
              </div>

              <div className="border border-forest/10 bg-white p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/60">Utile netto dopo la rata</p>
                <p
                  data-testid="risultato-utile"
                  className={`mt-3 flex items-center gap-2 font-display text-3xl font-extrabold ${
                    calc.utile >= 0 ? "text-moss" : "text-red-700"
                  }`}
                >
                  {calc.utile >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                  {fmt(calc.utile)}
                </p>
                <p className="mt-2 text-xs text-forest/55">Margine operativo 35% meno la rata mensile</p>
              </div>

              <div className="border border-forest/10 bg-white p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/60">Break-even</p>
                <p data-testid="risultato-breakeven" className="mt-3 font-display text-3xl font-extrabold text-forest">
                  {calc.breakEven} scontrini/giorno
                </p>
                <p className="mt-2 text-xs text-forest/55">Per coprire la rata con il margine operativo</p>
              </div>

              <div className="border border-forest/10 bg-white p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/60">Payback</p>
                <p data-testid="risultato-payback" className="mt-3 font-display text-3xl font-extrabold text-forest">
                  {calc.payback ? `${calc.payback} mesi` : "—"}
                </p>
                <p className="mt-2 text-xs text-forest/55">
                  {calc.payback
                    ? "Per rientrare dell'intero investimento"
                    : "Aumenta scontrini o ticket per rientrare"}
                </p>
              </div>
            </div>

            <div className="mt-6 border border-forest/10 bg-white p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-forest/60">Ammortamento — primi 6 mesi</p>
              <div data-testid="piano-ammortamento" className="mt-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-forest/10 text-left text-xs uppercase tracking-[0.15em] text-forest/50">
                      <th className="pb-3 pr-4">Mese</th>
                      <th className="pb-3 pr-4">Rata</th>
                      <th className="pb-3 pr-4">Interessi</th>
                      <th className="pb-3 pr-4">Capitale</th>
                      <th className="pb-3">Residuo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.piano.map((p) => (
                      <tr key={p.m} className="border-b border-forest/5 text-forest/80">
                        <td className="py-3 pr-4 font-medium">{p.m}</td>
                        <td className="py-3 pr-4">{fmt(p.rata)}</td>
                        <td className="py-3 pr-4">{fmt(p.interessi)}</td>
                        <td className="py-3 pr-4">{fmt(p.capitale)}</td>
                        <td className="py-3">{fmt(p.saldo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Link
              to="/affiliazione"
              data-testid="simulatore-cta"
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-moss px-10 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30"
            >
              Richiedi il business plan
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Simulatore;
